import {
  DOCUMENT_NATURE_OPTIONS,
  documentNatureLabel,
} from "@etudescarre/constants";
import { cn, Select } from "@etudescarre/ui";
import { isNotNil, joinListParam, splitListParam } from "@etudescarre/utils";
import { Funnel as FunnelIcon, X as XIcon } from "@phosphor-icons/react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router";
import type {
  DocumentData,
  DocumentListFilters,
  LotData,
  PrestaData,
} from "./types";
import {
  documentFilterPreferences,
  useFilterPreferencesSync,
} from "./use-filter-preferences-sync";

export type FilterOption = {
  label: string;
  value: string;
};

export type FilterBarProps = {
  lots: LotData[];
  /** Options du filtre "Documents diffusés à" (équipes destinataires). */
  diffuseATeamOptions: FilterOption[];
  /**
   * Utilisateur disposant d'un contrat travaux (comme `canMarkBpe` ailleurs
   * sur cet écran). N158 retire aux donneurs d'ordre les remontées à la
   * dernière diffusion et « Documents à mettre à jour uniquement ».
   */
  canMarkBpe: boolean;
};

function buildLotOptions(lots: LotData[]): FilterOption[] {
  return lots.map((l) => ({
    label: l.nLot ? `${l.nLot} - ${l.name}` : l.name,
    value: String(l.id),
  }));
}

function buildPrestaOptions(lots: LotData[]): FilterOption[] {
  const prestas: FilterOption[] = [];
  const seen = new Set<number>();
  for (const lot of lots) {
    for (const presta of lot.prestas) {
      if (!seen.has(presta.id)) {
        seen.add(presta.id);
        prestas.push({ label: presta.name, value: String(presta.id) });
      }
    }
  }
  return prestas;
}

export function buildNatureOptions(lots: LotData[]): FilterOption[] {
  const natures = new Set<string>();
  for (const lot of lots) {
    for (const presta of lot.prestas) {
      for (const doc of presta.documents) {
        if (isNotNil(doc.nature)) natures.add(doc.nature);
      }
    }
  }
  return DOCUMENT_NATURE_OPTIONS.filter((option) => natures.has(option.value));
}

// Mode de filtrage par indice -- selection exclusive (pas un
// multi-select de valeurs d'indice individuelles). Par defaut (aucune
// selection), tous les indices sont affiches.
// Les deux remontees ne sont proposees qu'aux utilisateurs disposant d'un
// contrat travaux (cf. `canMarkBpe`) : N158 les retire aux donneurs d'ordre.
export function buildIndexModeOptions(canMarkBpe: boolean): FilterOption[] {
  const recent: FilterOption = {
    label: "Indices récents uniquement",
    value: "recent3",
  };
  return canMarkBpe
    ? [
        recent,
        { label: "Remonter à la dernière diffusion pour avis", value: "avis" },
        {
          label: "Remonter à la dernière diffusion pour coordination",
          value: "coordination",
        },
      ]
    : [recent];
}

export function buildAuthorOptions(lots: LotData[]): FilterOption[] {
  const authors = new Set<string>();
  for (const lot of lots) {
    for (const presta of lot.prestas) {
      for (const doc of presta.documents) {
        if (doc.authorCompanyName) authors.add(doc.authorCompanyName);
      }
    }
  }
  return Array.from(authors)
    .sort((a, b) => a.localeCompare(b))
    .map((a) => ({ label: a, value: a }));
}

// N° : liste des numéros existants triés alphabétiquement, libellés avec
// l'intitulé du document. Le Select est rendu `searchable` ci-dessous pour
// la recherche incrémentale par préfixe.
export function buildNumberOptions(lots: LotData[]): FilterOption[] {
  const numbers = new Map<string, string>();
  for (const lot of lots) {
    for (const presta of lot.prestas) {
      for (const doc of presta.documents) {
        if (doc.number && !numbers.has(doc.number)) {
          numbers.set(doc.number, doc.name);
        }
      }
    }
  }
  return Array.from(numbers.entries())
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([number, name]) => ({
      label: name ? `${number} - ${name}` : number,
      value: number,
    }));
}

function buildNameOptions(lots: LotData[]): FilterOption[] {
  const names = new Set<string>();
  for (const lot of lots) {
    for (const presta of lot.prestas) {
      for (const doc of presta.documents) {
        if (doc.name) names.add(doc.name);
      }
    }
  }
  return Array.from(names)
    .sort((a, b) => a.localeCompare(b))
    .map((n) => ({ label: n, value: n }));
}

// Niveaux/Façades : construit à partir des noms de zones présents dans les
// documents chargés (zone.name côté API).
export function buildZoneNameOptions(lots: LotData[]): FilterOption[] {
  const zones = new Set<string>();
  for (const lot of lots) {
    for (const presta of lot.prestas) {
      for (const doc of presta.documents) {
        for (const z of doc.zones) {
          if (z) zones.add(z);
        }
      }
    }
  }
  return Array.from(zones)
    .sort((a, b) => a.localeCompare(b))
    .map((z) => ({ label: z, value: z }));
}

// Sélection d'un lot => sélection de toutes ses prestations.
export function prestasForLots(lots: LotData[], lotIds: number[]): string[] {
  const selected = new Set(lotIds);
  const prestaIds = new Set<string>();
  for (const lot of lots) {
    if (!selected.has(lot.id)) continue;
    for (const presta of lot.prestas) {
      prestaIds.add(String(presta.id));
    }
  }
  return Array.from(prestaIds);
}

type FacetKey =
  | "lot"
  | "presta"
  | "author"
  | "number"
  | "nature"
  | "name"
  | "zoneName";

const FACET_SCOPES: Record<FacetKey, FacetKey[]> = {
  lot: ["author"],
  presta: ["lot", "author"],
  author: ["lot", "presta"],
  number: ["lot", "presta", "author", "nature", "name", "zoneName"],
  nature: ["lot", "presta", "author", "number", "name", "zoneName"],
  name: ["lot", "presta", "author", "number", "nature", "zoneName"],
  zoneName: ["lot", "presta", "author", "number", "nature", "name"],
};

function matchesFacet(
  filters: DocumentListFilters,
  key: FacetKey,
  lot: LotData,
  presta: PrestaData,
  doc: DocumentData,
): boolean {
  switch (key) {
    case "lot":
      return filters.lot.includes(lot.id);
    case "presta":
      return filters.presta.includes(presta.id);
    case "author":
      return filters.author.includes(doc.authorCompanyName);
    case "number":
      return filters.number.includes(doc.number);
    case "nature":
      return isNotNil(doc.nature) && filters.nature.includes(doc.nature);
    case "name":
      return filters.name.includes(doc.name);
    case "zoneName":
      return doc.zones.some((zoneName) => filters.zoneName.includes(zoneName));
  }
}

const PROVENANCE_FACETS: ReadonlySet<FacetKey> = new Set([
  "lot",
  "presta",
  "author",
]);

// Les deux cases sont rangées dans le bloc "Caractéristiques document" :
// elles restreignent ses listes, jamais celles du bloc "Provenance".
function matchesNoticeCheckboxes(
  filters: DocumentListFilters,
  doc: DocumentData,
): boolean {
  if (filters.hideObsolete && doc.noticeFlag === "obsolete") return false;
  if (filters.needsUpdate && doc.noticeFlag !== "nouvel_indice") return false;
  return true;
}

export function narrowLotsForFacet(
  lots: LotData[],
  filters: DocumentListFilters,
  key: FacetKey,
): LotData[] {
  const scope = FACET_SCOPES[key].filter((k) => filters[k].length > 0);
  const checkboxesApply = !PROVENANCE_FACETS.has(key);
  return lots
    .map((lot) => ({
      ...lot,
      prestas: lot.prestas
        .map((presta) => ({
          ...presta,
          documents: presta.documents.filter(
            (doc) =>
              (!checkboxesApply || matchesNoticeCheckboxes(filters, doc)) &&
              scope.every((k) => matchesFacet(filters, k, lot, presta, doc)),
          ),
        }))
        .filter((presta) => presta.documents.length > 0),
    }))
    .filter((lot) => lot.prestas.length > 0);
}

export function facetOptions(
  build: (lots: LotData[]) => FilterOption[],
  lots: LotData[],
  filters: DocumentListFilters,
  key: FacetKey,
  labelFor: (value: string) => string = (value) => value,
): FilterOption[] {
  const selected = filters[key].map(String);
  const available = new Set(
    build(narrowLotsForFacet(lots, filters, key)).map((option) => option.value),
  );
  const options = build(lots).filter(
    (option) => available.has(option.value) || selected.includes(option.value),
  );
  const unlisted = selected.filter(
    (value) => !options.some((option) => option.value === value),
  );
  return [
    ...options,
    ...unlisted.map((value) => ({ label: labelFor(value), value })),
  ];
}

function parseSearchParamIds(param: string | null): number[] {
  if (!param) return [];
  return param
    .split(",")
    .map(Number)
    .filter((n) => Number.isFinite(n) && n > 0);
}

// Filtres du bloc "Caractéristiques document" dont les valeurs proposées
// dépendent du bloc "Provenance" (lot, presta, auteur) : un changement en
// Provenance les réinitialise, pas l'inverse. Indices et les deux cases ne
// dépendent pas de la Provenance et restent.
export const CASCADE_RESET_KEYS = [
  "nature",
  "number",
  "name",
  "zoneName",
] as const satisfies ReadonlyArray<keyof DocumentListFilters>;

export function resetCascadeParams(params: URLSearchParams) {
  for (const key of CASCADE_RESET_KEYS) {
    params.delete(key);
  }
}

// Serializes a full DocumentListFilters (e.g. loaded from saved preferences)
// into the URL search params encoding used throughout this component.
function filtersToSearchParams(filters: DocumentListFilters): URLSearchParams {
  const next = new URLSearchParams();
  if (filters.lot.length > 0) next.set("lot", filters.lot.join(","));
  if (filters.presta.length > 0) next.set("presta", filters.presta.join(","));
  if (filters.nature.length > 0)
    next.set("nature", joinListParam(filters.nature));
  if (filters.zone.length > 0) next.set("zone", filters.zone.join(","));
  if (filters.indexMode.length > 0)
    next.set("indexMode", filters.indexMode.join(","));
  if (filters.author.length > 0)
    next.set("author", joinListParam(filters.author));
  if (filters.number.length > 0)
    next.set("number", joinListParam(filters.number));
  if (filters.name.length > 0) next.set("name", joinListParam(filters.name));
  if (filters.zoneName.length > 0)
    next.set("zoneName", joinListParam(filters.zoneName));
  if (filters.diffuseA.length > 0)
    next.set("diffuseA", filters.diffuseA.join(","));
  if (filters.hideObsolete) next.set("hideObsolete", "1");
  if (filters.needsUpdate) next.set("needsUpdate", "1");
  return next;
}

export function FilterBar({
  lots,
  diffuseATeamOptions,
  canMarkBpe,
}: FilterBarProps) {
  const { yardId } = useParams<{ yardId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [visible, setVisible] = useState(true);
  const lastScrollY = useRef(0);

  // Read filters from URL
  const filters: DocumentListFilters = {
    lot: parseSearchParamIds(searchParams.get("lot")),
    presta: parseSearchParamIds(searchParams.get("presta")),
    nature: splitListParam(searchParams.get("nature")),
    zone: parseSearchParamIds(searchParams.get("zone")),
    indexMode: splitListParam(searchParams.get("indexMode")),
    author: splitListParam(searchParams.get("author")),
    number: splitListParam(searchParams.get("number")),
    name: splitListParam(searchParams.get("name")),
    zoneName: splitListParam(searchParams.get("zoneName")),
    diffuseA: parseSearchParamIds(searchParams.get("diffuseA")),
    hideObsolete: searchParams.get("hideObsolete") === "1",
    needsUpdate: searchParams.get("needsUpdate") === "1",
  };

  const hasActiveFilters =
    filters.lot.length > 0 ||
    filters.presta.length > 0 ||
    filters.nature.length > 0 ||
    filters.zone.length > 0 ||
    filters.indexMode.length > 0 ||
    filters.author.length > 0 ||
    filters.number.length > 0 ||
    filters.name.length > 0 ||
    filters.zoneName.length > 0 ||
    filters.diffuseA.length > 0 ||
    filters.hideObsolete ||
    filters.needsUpdate;

  // Load the caller's saved filters (per user, per yard) when the URL
  // starts empty, and persist every subsequent change server-side.
  useFilterPreferencesSync(
    yardId,
    filters,
    searchParams.toString(),
    hasActiveFilters,
    useCallback(
      (loaded: DocumentListFilters) => {
        setSearchParams(filtersToSearchParams(loaded), { replace: true });
      },
      [setSearchParams],
    ),
    documentFilterPreferences,
  );

  const updateFilter = useCallback(
    (
      key: keyof DocumentListFilters,
      values: string[],
      options?: { cascade?: boolean },
    ) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (values.length > 0) {
            next.set(key, joinListParam(values));
          } else {
            next.delete(key);
          }
          if (options?.cascade) {
            resetCascadeParams(next);
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  // La sélection d'un lot implique la sélection de toutes ses prestations, et
  // réinitialise les CASCADE_RESET_KEYS.
  const updateLotFilter = useCallback(
    (values: string[]) => {
      const lotIds = values
        .map(Number)
        .filter((n) => Number.isFinite(n) && n > 0);
      const prestaValues = prestasForLots(lots, lotIds);
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (values.length > 0) {
            next.set("lot", values.join(","));
          } else {
            next.delete("lot");
          }
          if (prestaValues.length > 0) {
            next.set("presta", prestaValues.join(","));
          } else {
            next.delete("presta");
          }
          resetCascadeParams(next);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams, lots],
  );

  const toggleHideObsolete = useCallback(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (next.get("hideObsolete") === "1") {
          next.delete("hideObsolete");
        } else {
          next.set("hideObsolete", "1");
        }
        return next;
      },
      { replace: true },
    );
  }, [setSearchParams]);

  const toggleNeedsUpdate = useCallback(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (next.get("needsUpdate") === "1") {
          next.delete("needsUpdate");
        } else {
          next.set("needsUpdate", "1");
        }
        return next;
      },
      { replace: true },
    );
  }, [setSearchParams]);

  // Hide filter bar on scroll down, show on scroll up
  useEffect(() => {
    function handleScroll() {
      const currentY = window.scrollY;
      if (currentY > lastScrollY.current && currentY > 100) {
        setVisible(false);
      } else if (currentY < lastScrollY.current) {
        setVisible(true);
      }
      lastScrollY.current = currentY;
    }

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const clearFilters = useCallback(() => {
    setSearchParams({}, { replace: true });
  }, [setSearchParams]);

  return (
    <>
      {/* Floating button when filter bar is hidden */}
      {!visible && (
        <button
          type="button"
          onClick={() => setVisible(true)}
          className="fixed top-20 right-4 z-30 flex items-center gap-2 rounded-lg bg-white px-3 py-2 text-sm font-medium text-zinc-700 shadow-lg border border-zinc-200 hover:bg-zinc-50 dark:bg-zinc-800 dark:text-zinc-200 dark:border-zinc-700 dark:hover:bg-zinc-700"
        >
          <FunnelIcon size={16} />
          Filtres
          {hasActiveFilters && (
            <span className="ml-1 flex h-5 w-5 items-center justify-center rounded-full bg-blue-600 text-xs text-white">
              {filters.lot.length +
                filters.presta.length +
                filters.nature.length +
                filters.indexMode.length +
                filters.author.length +
                filters.number.length +
                filters.name.length +
                filters.zoneName.length +
                filters.diffuseA.length +
                (filters.hideObsolete ? 1 : 0) +
                (filters.needsUpdate ? 1 : 0)}
            </span>
          )}
        </button>
      )}

      {/* Filter bar */}
      <div
        className={cn(
          "sticky top-0 z-20 bg-white border-b border-zinc-200 dark:bg-zinc-900 dark:border-zinc-700 transition-all duration-200",
          visible
            ? "translate-y-0 opacity-100"
            : "-translate-y-full opacity-0 pointer-events-none",
        )}
      >
        <div className="flex flex-wrap items-start gap-3 px-4 py-3">
          <FunnelIcon size={18} className="mt-2.5 text-zinc-500 shrink-0" />

          <div className="flex flex-1 flex-wrap items-start gap-3 min-w-0">
            {/* Bloc "Provenance" : lot / prestation / auteur. */}
            <fieldset className="rounded border border-zinc-200 px-3 py-2 dark:border-zinc-700">
              <legend className="px-1 text-xs font-semibold uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
                Filtres sur provenances
              </legend>
              <div className="flex flex-wrap items-center gap-2">
                <div className="w-44">
                  <Select
                    options={facetOptions(
                      buildLotOptions,
                      lots,
                      filters,
                      "lot",
                    )}
                    value={filters.lot.map(String)}
                    onValueChange={({ value }) => updateLotFilter(value)}
                    placeholder="Lots"
                    multiple
                  />
                </div>

                <div className="w-44">
                  <Select
                    options={facetOptions(
                      buildPrestaOptions,
                      lots,
                      filters,
                      "presta",
                    )}
                    value={filters.presta.map(String)}
                    onValueChange={({ value }) =>
                      updateFilter("presta", value, { cascade: true })
                    }
                    placeholder="Prestations"
                    multiple
                  />
                </div>

                <div className="w-44">
                  <Select
                    options={facetOptions(
                      buildAuthorOptions,
                      lots,
                      filters,
                      "author",
                    )}
                    value={filters.author}
                    onValueChange={({ value }) =>
                      updateFilter("author", value, { cascade: true })
                    }
                    placeholder="Auteurs"
                    multiple
                  />
                </div>
              </div>
            </fieldset>

            {/* Bloc "Caractéristiques document" : n°, nature, indice, intitulé,
                niveaux/façades + les 2 caractéristiques booléennes. */}
            <fieldset className="rounded border border-zinc-200 px-3 py-2 dark:border-zinc-700">
              <legend className="px-1 text-xs font-semibold uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
                Filtres sur caractéristiques document
              </legend>
              <div className="flex flex-wrap items-center gap-2">
                <div className="w-52">
                  <Select
                    options={facetOptions(
                      buildNumberOptions,
                      lots,
                      filters,
                      "number",
                    )}
                    value={filters.number}
                    onValueChange={({ value }) => updateFilter("number", value)}
                    placeholder="N°"
                    multiple
                    searchable
                  />
                </div>

                <div className="w-44">
                  <Select
                    options={facetOptions(
                      buildNatureOptions,
                      lots,
                      filters,
                      "nature",
                      documentNatureLabel,
                    )}
                    value={filters.nature}
                    onValueChange={({ value }) => updateFilter("nature", value)}
                    placeholder="Natures"
                    multiple
                  />
                </div>

                <div className="w-64">
                  <Select
                    options={buildIndexModeOptions(canMarkBpe)}
                    value={filters.indexMode}
                    onValueChange={({ value }) =>
                      updateFilter("indexMode", value)
                    }
                    placeholder="Indices"
                  />
                </div>

                <div className="w-44">
                  <Select
                    options={facetOptions(
                      buildNameOptions,
                      lots,
                      filters,
                      "name",
                    )}
                    value={filters.name}
                    onValueChange={({ value }) => updateFilter("name", value)}
                    placeholder="Intitulés"
                    multiple
                  />
                </div>

                <div className="w-44">
                  <Select
                    options={facetOptions(
                      buildZoneNameOptions,
                      lots,
                      filters,
                      "zoneName",
                    )}
                    value={filters.zoneName}
                    onValueChange={({ value }) =>
                      updateFilter("zoneName", value)
                    }
                    placeholder="Niveaux/Façades"
                    multiple
                  />
                </div>

                {/* Restreint les destinataires pris en compte pour le picto
                    "visa en retard" ; les versions non diffusées à la sélection
                    s'estompent. */}
                <div className="w-44">
                  <Select
                    options={diffuseATeamOptions}
                    value={filters.diffuseA.map(String)}
                    onValueChange={({ value }) =>
                      updateFilter("diffuseA", value)
                    }
                    placeholder="Diffusés à"
                    multiple
                  />
                </div>

                <label className="flex items-center gap-2 text-sm text-zinc-700 dark:text-zinc-300 whitespace-nowrap cursor-pointer">
                  <input
                    type="checkbox"
                    checked={filters.hideObsolete}
                    onChange={toggleHideObsolete}
                    className="h-4 w-4 rounded border-zinc-300 text-blue-600 focus:ring-blue-500"
                  />
                  Masquer les obsolètes
                </label>

                {canMarkBpe && (
                  <label className="flex items-center gap-2 text-sm text-zinc-700 dark:text-zinc-300 whitespace-nowrap cursor-pointer">
                    <input
                      type="checkbox"
                      checked={filters.needsUpdate}
                      onChange={toggleNeedsUpdate}
                      className="h-4 w-4 rounded border-zinc-300 text-blue-600 focus:ring-blue-500"
                    />
                    Documents à mettre à jour uniquement
                  </label>
                )}
              </div>
            </fieldset>
          </div>

          {hasActiveFilters && (
            <button
              type="button"
              onClick={clearFilters}
              className="mt-2.5 flex items-center gap-1 rounded px-2 py-1 text-xs text-zinc-500 hover:text-zinc-700 hover:bg-zinc-100 dark:text-zinc-400 dark:hover:text-zinc-200 dark:hover:bg-zinc-800"
            >
              <XIcon size={14} />
              Effacer
            </button>
          )}
        </div>
      </div>
    </>
  );
}
